import React from "react";
import {
  MediaModalModel,
  MediaModalItemModel,
  MediaModalImageSlideModel,
} from "../../models/MediaModalModel";
import { MediaType } from "../../models/MediaType";
import { enableBodyScroll, disableBodyScroll } from "../../utils/dom";
import ImgLazyLoad from "../ImgLazyLoad";

interface MediaModalState {
  content: MediaModalModel;
  currentItemIndex: number;
  currentSlideIndex: number;
  isVisible: boolean;
}

class MediaModal extends React.Component<{}, MediaModalState> {
  get currentItem(): MediaModalItemModel {
    const { currentItemIndex, content } = this.state;
    return content ? content[this.state.currentItemIndex] : null;
  }

  get currentSlideContent(): MediaModalImageSlideModel {
    const content = this.currentItem;
    return content.slides ? content.slides[this.state.currentSlideIndex] : null;
  }

  constructor(props) {
    super(props);

    this.state = {
      content: null,
      currentItemIndex: 0,
      currentSlideIndex: 0,
      isVisible: false,
    };

    window.addEventListener("showMediaModal", (e: any) => {
      const { content, startIndex } = e.detail;
      const isVisible = true;
      const currentItemIndex = startIndex || 0;
      const currentSlideIndex = 0;
      disableBodyScroll();

      this.setState({
        content,
        isVisible,
        currentItemIndex,
        currentSlideIndex,
      });
    });

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.close = this.close.bind(this);
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  handleKeyDown(e): boolean {
    if (this.state.isVisible && e.keyCode === 27) {
      this.close();
    }

    return true;
  }

  close() {
    const isVisible = false;
    const content = null;

    enableBodyScroll();
    this.setState({ content, isVisible });
  }

  prev() {
    let currentSlideIndex = this.state.currentSlideIndex - 1;
    if (currentSlideIndex < 0)
      currentSlideIndex = this.currentItem.slides.length - 1;

    this.setState({ currentSlideIndex });
  }

  next() {
    let currentSlideIndex = this.state.currentSlideIndex + 1;
    if (currentSlideIndex >= this.currentItem.slides.length)
      currentSlideIndex = 0;

    this.setState({ currentSlideIndex });
  }

  renderVideoContent() {
    return (
      <div className="mediaModal__videoDiv">
        <video
          className="mediaModal__video"
          controls={true}
          autoPlay={true}
          loop={false}
          preload="auto"
          src={this.currentItem.media}
        />
      </div>
    );
  }

  renderYouTubeContent() {
    let url = this.currentItem.media;

    const videoID = this.extractVideoID(url);
    if (!videoID) {
      console.error("Invalid YouTube URL");
      return null;
    }

    url = `https://www.youtube.com/embed/${videoID}`;

    if (url.indexOf("?") === -1) {
      url += "?rel=0&autoplay=1";
    } else {
      url += "&rel=0&autoplay=1";
    }

    return (
      <div className="mediaModal__youtubeDiv">
        <iframe
          className="mediaModal__iframe"
          width="100%"
          height="100%"
          src={url}
          frameBorder="0"
          allowFullScreen={true}
        />
      </div>
    );
  }

  extractVideoID(url) {
    const regExp =
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  }

  renderImageContent() {
    const content = this.currentItem;

    return content.slides ? (
      this.renderSlideContent()
    ) : (
      <div
        className="mediaModal__imgDiv"
        style={{ backgroundImage: `url("${content.media}")` }}
      >
        <ImgLazyLoad className="mediaModal__img" src={content.media} alt="" />
      </div>
    );
  }

  renderTextContent() {
    const content = this.currentItem;

    return (
      <div className="mediaModal__textDiv">
        <div dangerouslySetInnerHTML={{ __html: content.text }} />
      </div>
    );
  }

  renderSlideContent() {
    return (
      <div
        className="mediaModal__imgDiv"
        style={{ backgroundImage: `url("${this.currentSlideContent.media}")` }}
      >
        <ImgLazyLoad
          className="mediaModal__img"
          src={this.currentSlideContent.media}
          alt=""
        />
        <div className="mediaModal__slideCopy">
          <h4 className="h4 mediaModal__slideTitle">
            {this.currentSlideContent.title}
          </h4>
          <div
            className="mediaModal__slideText"
            dangerouslySetInnerHTML={{ __html: this.currentSlideContent.text }}
          />
        </div>
        <div className="mediaModal__controls">
          <div>
            <div
              className="mediaModal__controlWrapper"
              onClick={(e) => {
                e.preventDefault();
                this.prev();
              }}
            >
              <div className="mediaModal__control mediaModal__control--prev" />
            </div>
          </div>

          <div>
            <div
              className="mediaModal__controlWrapper"
              onClick={(e) => {
                e.preventDefault();
                this.next();
              }}
            >
              <div className="mediaModal__control mediaModal__control--next" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isVisible } = this.state;
    const content = this.currentItem;

    return (
      <section
        className={`mediaModal ${isVisible ? "mediaModal--visible" : ""} `}
      >
        {content ? (
          <div className="mediaModal__wrap">
            <div className="mediaModal__closeBtn" onClick={this.close}>
              Close Modal
            </div>

            {content.type === MediaType.YouTube && this.renderYouTubeContent()}
            {content.type === MediaType.Video && this.renderVideoContent()}
            {content.type === MediaType.Image && this.renderImageContent()}
            {content.type === MediaType.Text && this.renderTextContent()}

            <div
              className={
                content.type === MediaType.YouTube
                  ? "mediaModal__youtubeCopy"
                  : "mediaModal__copy"
              }
            >
              {content.title &&
                (content.hideHeading == null ||
                content.hideHeading === false ? (
                  <h3
                    className="h3 mediaModal__title"
                    dangerouslySetInnerHTML={{ __html: content.title }}
                  />
                ) : null)}
              {content.text && (
                <div
                  className="mediaModal__text"
                  dangerouslySetInnerHTML={{ __html: content.text }}
                />
              )}
            </div>
          </div>
        ) : null}
      </section>
    );
  }
}

export default MediaModal;
